import React from "react";
import { Box, InputAdornment, Link, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useAuth } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";
import { C } from "../../common/theme";
import { formatAge, formatCount } from "../../common/utils/format";
import { ChatMonitorRow, getChatHistory, getChatMonitorStats, getMonitoredChats } from "../../common/requests/chats/endpoints";
import {
  Banner,
  Column,
  CopyValue,
  DataTable,
  DetailDialog,
  DetailList,
  DetailSection,
  EmptyState,
  ErrorState,
  KpiCard,
  KpiStrip,
  LoadingRows,
  PageShell,
  useIsNarrow,
  useResource,
  useUrlParam,
} from "../../ui";
import {
  buyerName,
  findChat,
  formatDateTime,
  resolveSenderName,
  sellerName,
  senderSide,
  sortMessages,
  toMillis,
  truncateMiddle,
} from "./chatUtils";

const REFRESH_INTERVAL_MS = 15000;
const RECENT_CHAT_LIMIT = 10;
const FILTER_DEBOUNCE_MS = 300;
const CARD_BELOW = 760;

/** Marketplace profile link; stops the click from also opening the transcript.
 * Plain text inside phone cards (the whole card is a button). */
function ProfileName({ username, fallback, plain }: { username: string | null; fallback: string | null; plain?: boolean }) {
  if (!username || plain) return <>{username || fallback || "–"}</>;
  return (
    <Link
      href={`https://inflos.co/u/${encodeURIComponent(username)}`}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{ fontWeight: 600 }}
    >
      {username}
    </Link>
  );
}

/** Read-only view of buyer/seller chats: the most recently active ones, with
 * buyer and seller filters, and the full transcript in a dialog. */
export default function ChatMonitor() {
  const { idToken } = useAuth();
  const narrow = useIsNarrow(CARD_BELOW);
  const [buyerFilter, setBuyerFilter] = React.useState("");
  const [sellerFilter, setSellerFilter] = React.useState("");
  const [buyer, setBuyer] = React.useState("");
  const [seller, setSeller] = React.useState("");
  const [chatParam, setChatParam] = useUrlParam("chat");
  const chatId = (chatParam || "").trim() || null;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setBuyer(buyerFilter.trim());
      setSeller(sellerFilter.trim());
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [buyerFilter, sellerFilter]);
  const isFiltering = !!(buyer || seller);

  // Polls only while the tab is visible (each refresh hits Firestore) and
  // refreshes on return.
  const feed = useResource(
    async () => {
      const [chats, stats] = await Promise.all([
        getMonitoredChats(idToken!, { limit: RECENT_CHAT_LIMIT, buyer, seller }),
        getChatMonitorStats(idToken!),
      ]);
      return { rows: chats.data || [], stats: stats.data || null, at: new Date() };
    },
    [idToken, buyer, seller],
    { enabled: !!idToken, pollMs: REFRESH_INTERVAL_MS, errorText: "Couldn't load the chat feed." }
  );
  const rows = React.useMemo(() => feed.data?.rows ?? [], [feed.data]);
  const stats = feed.data?.stats ?? null;

  const history = useResource(
    async () => {
      const res = await getChatHistory(chatId!, idToken!);
      return { chatId: chatId!, messages: sortMessages(res.data.messages || []), truncated: Boolean(res.data.truncated) };
    },
    [chatId, idToken],
    { enabled: !!chatId && !!idToken, errorText: "Couldn't load this chat." }
  );
  // useResource keeps the previous chat on screen while the next one loads.
  const transcript = history.data && history.data.chatId === chatId ? history.data : null;
  const openRow = findChat(rows, chatId);

  const transcriptEndRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (transcript?.messages.length) transcriptEndRef.current?.scrollIntoView({ block: "end" });
  }, [transcript]);

  const columns: Column<ChatMonitorRow>[] = [
    {
      key: "listing",
      header: "Listing",
      card: "title",
      minWidth: 180,
      sortValue: (r) => (r.listing_name || r.listing_id || "").toLowerCase(),
      render: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{r.listing_name || "Untitled listing"}</Box>
          {!narrow && (
            <Box sx={{ fontSize: 12.5, color: C.muted }}>
              {r.listing_name ? (
                <Link
                  component={RouterLink}
                  to={`${ROUTES.SALES}?q=${encodeURIComponent(r.listing_name)}`}
                  underline="hover"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                >
                  Find orders
                </Link>
              ) : (
                truncateMiddle(r.listing_id)
              )}
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: "buyer",
      header: "Buyer",
      card: "subtitle",
      sortValue: (r) => buyerName(r).toLowerCase(),
      render: (r) =>
        narrow ? (
          <>Buyer {buyerName(r)}</>
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <ProfileName username={r.buyer_username} fallback={r.buyer_email} />
            <Box sx={{ fontSize: 12.5, color: C.muted }}>{truncateMiddle(r.buyer_id)}</Box>
          </Box>
        ),
    },
    {
      key: "seller",
      header: "Seller",
      card: "subtitle",
      sortValue: (r) => sellerName(r).toLowerCase(),
      render: (r) =>
        narrow ? (
          <>Seller {sellerName(r)}</>
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <ProfileName username={r.seller_username} fallback={r.seller_email} />
            <Box sx={{ fontSize: 12.5, color: C.muted }}>{truncateMiddle(r.seller_id)}</Box>
          </Box>
        ),
    },
    {
      key: "started",
      header: "Started",
      card: "hidden",
      hideBelow: 1100,
      sortValue: (r) => toMillis(r.created_at),
      render: (r) => <Box sx={{ fontSize: 13, whiteSpace: "nowrap" }}>{formatDateTime(r.created_at)}</Box>,
    },
    {
      key: "last",
      header: "Last message",
      card: "aside",
      align: "right",
      sortValue: (r) => toMillis(r.last_message_at) || toMillis(r.created_at),
      render: (r) =>
        narrow ? (
          <Box sx={{ fontSize: 12.5, color: C.muted, whiteSpace: "nowrap" }}>{r.last_message_at ? formatAge(r.last_message_at) : "–"}</Box>
        ) : (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Box sx={{ fontSize: 13 }}>{formatDateTime(r.last_message_at)}</Box>
            {r.last_message_at && <Box sx={{ fontSize: 12.5, color: C.muted }}>{formatAge(r.last_message_at)}</Box>}
          </Box>
        ),
    },
  ];

  const filterField = (label: string, value: string, onChange: (v: string) => void) => (
    <TextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Username, email or user ID"
      sx={{ flex: "1 1 240px", minWidth: 0 }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );

  return (
    <PageShell title="Chats" subtitle="Read-only view of buyer and seller chats. Refreshes every 15 seconds while this tab is open.">
      <KpiStrip max={3}>
        <KpiCard title="Total chats" value={stats ? formatCount(stats.total) : "–"} />
        <KpiCard
          title="Open chats"
          value={stats ? formatCount(stats.active) : "–"}
          help="Chats that haven't been deleted. A chat reopens automatically when someone writes in it."
        />
        <KpiCard title="New in 24h" value={stats ? formatCount(stats.new_24h) : "–"} />
      </KpiStrip>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
        {filterField("Buyer", buyerFilter, setBuyerFilter)}
        {filterField("Seller", sellerFilter, setSellerFilter)}
      </Box>

      {feed.error && rows.length > 0 && (
        <Banner tone="danger" title="Refresh failed.">
          {feed.error} Showing the last chats loaded.
        </Banner>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <DataTable
          aria-label="Chats"
          rows={rows}
          columns={columns}
          getRowId={(r) => r.chat_id}
          loading={feed.status === "loading" || feed.status === "idle"}
          error={feed.error}
          onRetry={feed.reload}
          empty={
            <EmptyState
              icon={<ForumOutlinedIcon />}
              title={isFiltering ? "No chats match these filters." : "No chats yet."}
              body={isFiltering ? "Try a username, email or full user ID." : undefined}
            />
          }
          onRowClick={(r) => setChatParam((r.chat_id || "").trim(), true)}
          selectedId={openRow?.chat_id ?? null}
          initialSort={{ key: "last", dir: "desc" }}
          cardBelow={CARD_BELOW}
        />
        <Typography variant="caption" sx={{ color: C.muted }}>
          {isFiltering
            ? `Showing ${rows.length} matching chat${rows.length === 1 ? "" : "s"}`
            : `Showing the ${RECENT_CHAT_LIMIT} most recently active chats`}
          {" · "}Last updated {feed.data ? formatDateTime(feed.data.at) : "–"}
        </Typography>
      </Box>

      <DetailDialog
        open={!!chatId}
        onClose={() => setChatParam(null)}
        title={openRow?.listing_name || "Chat"}
        subtitle={openRow ? `${buyerName(openRow)} with ${sellerName(openRow)}` : undefined}
        size="md"
      >
        <DetailSection title="Chat">
          <DetailList
            items={[
              { label: "Buyer", value: openRow ? <ProfileName username={openRow.buyer_username} fallback={openRow.buyer_email} /> : "–" },
              { label: "Seller", value: openRow ? <ProfileName username={openRow.seller_username} fallback={openRow.seller_email} /> : "–" },
              {
                label: "Listing",
                value: openRow?.listing_name ? (
                  <Link component={RouterLink} to={`${ROUTES.SALES}?q=${encodeURIComponent(openRow.listing_name)}`} underline="hover">
                    Find orders for this listing
                  </Link>
                ) : openRow ? (
                  <CopyValue value={openRow.listing_id} mono />
                ) : (
                  "–"
                ),
              },
              { label: "Chat ID", value: chatId ? <CopyValue value={chatId} mono /> : "–" },
              { label: "Started", value: openRow ? formatDateTime(openRow.created_at) : "–" },
              { label: "Last message", value: openRow ? formatDateTime(openRow.last_message_at) : "–" },
            ]}
          />
          {!openRow && rows.length > 0 && (
            <Typography variant="caption" sx={{ color: C.muted }}>
              This chat isn't in the list on the page, so senders show as user IDs.
            </Typography>
          )}
        </DetailSection>

        <DetailSection title={transcript ? `Messages (${transcript.messages.length})` : "Messages"}>
          {history.status === "error" && !transcript ? (
            <ErrorState message={history.error} onRetry={history.reload} />
          ) : !transcript ? (
            <LoadingRows rows={4} height={56} />
          ) : transcript.messages.length === 0 ? (
            <EmptyState icon={<ForumOutlinedIcon />} title="No messages in this chat." />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {transcript.truncated && (
                <Typography variant="caption" sx={{ color: C.muted }}>
                  Showing the latest {transcript.messages.length} messages. Older messages aren't shown.
                </Typography>
              )}
              {transcript.messages.map((item, index) => {
                const side = senderSide(item, openRow);
                return (
                  <Box
                    key={item.message_id || `${item.timestamp}-${index}`}
                    sx={{
                      alignSelf: side === "seller" ? "flex-end" : "flex-start",
                      maxWidth: { xs: "92%", sm: "78%" },
                      minWidth: 0,
                      bgcolor: side === "seller" ? C.leafTint : side === "buyer" ? C.ground : C.bone1,
                      border: `1px solid ${C.line}`,
                      borderRadius: "8px",
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 0.25 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: C.ink }}>
                        {resolveSenderName(item, openRow)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.muted }}>
                        {formatDateTime(item.timestamp)}
                        {item.message_type && item.message_type !== "text" ? ` · ${item.message_type}` : ""}
                      </Typography>
                    </Box>
                    {item.message_type === "image" && item.content ? (
                      <a href={item.content} target="_blank" rel="noreferrer">
                        <Box
                          component="img"
                          src={item.content}
                          alt="Sent image"
                          loading="lazy"
                          sx={{ maxWidth: "100%", maxHeight: 220, borderRadius: "6px", display: "block" }}
                        />
                      </a>
                    ) : (
                      <Typography variant="body2" sx={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
                        {item.content || "(empty message)"}
                      </Typography>
                    )}
                  </Box>
                );
              })}
              <div ref={transcriptEndRef} />
            </Box>
          )}
        </DetailSection>
      </DetailDialog>
    </PageShell>
  );
}
